/**
 * The only place this application writes to the device.
 *
 * Everything about it is deliberately small. It holds four things — a preference, a history of
 * which questions you keep getting wrong, at most one deliberately saved session, and a draft of
 * the exam in progress. The first three are written only after somebody asks for them; the fourth
 * lives in the tab and dies with it. Nothing here is ever transmitted: there is no server to
 * transmit to, and the Content Security Policy blocks the page from reaching one.
 *
 * The browser's own stores rather than IndexedDB, for a reason worth stating: the data is a few
 * kilobytes of plain objects, it must be readable synchronously while deciding what to render and
 * while the page is being torn down, and it must be possible for a person to open their browser's
 * storage inspector and see exactly what is kept. IndexedDB would win on size, and size is the one
 * thing this store does not need — exam files are never stored, only ids and counts.
 *
 * Storage can also simply not be there: private windows, disabled cookies, exhausted quota. Every
 * one of those throws on access rather than returning null, so every access is guarded and a
 * failure degrades to "this device does not remember", never to a broken screen.
 */

const PREFIX = 'freexam:v1:';

export type StoreKey = 'preferences' | 'history' | 'saved-session' | 'draft';

/**
 * Which of the browser's two stores to use, and why there are two.
 *
 * `local` outlives the browser: it is where a preference or a deliberately saved session goes, and
 * nothing is written there until somebody asks for it.
 *
 * `session` belongs to the tab and dies with it. That is not a lesser version of the first one —
 * it is a different promise. A draft of the exam in progress lives there precisely because it must
 * survive a stray refresh and must **not** survive closing the tab, which is what lets the privacy
 * document keep saying that closing the tab discards the session.
 */
export type StoreArea = 'local' | 'session';

function backing(area: StoreArea): Storage | null {
  try {
    const storage = area === 'local' ? window.localStorage : window.sessionStorage;
    // Reading is not enough: Safari's private mode used to allow reads and throw on write, and a
    // store that discovers it is read-only halfway through a save is worse than one that knows.
    const probe = `${PREFIX}probe`;
    storage.setItem(probe, '1');
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

/** Whether this device can remember anything at all. The UI says so rather than failing silently. */
export function isStorageAvailable(): boolean {
  return backing('local') !== null;
}

export function read<T>(
  key: StoreKey,
  fallback: T,
  revive: (value: unknown) => T | null,
  area: StoreArea = 'local',
): T {
  const storage = backing(area);
  if (storage === null) {
    return fallback;
  }

  try {
    const raw = storage.getItem(PREFIX + key);
    if (raw === null) {
      return fallback;
    }
    // Anything already on the device is treated exactly like an imported file: it was written by a
    // previous version, or by hand, and it is checked before it is believed.
    return revive(JSON.parse(raw)) ?? fallback;
  } catch {
    return fallback;
  }
}

/** Returns false when the value could not be stored, so a caller can tell the user rather than lie. */
export function write(key: StoreKey, value: unknown, area: StoreArea = 'local'): boolean {
  const storage = backing(area);
  if (storage === null) {
    return false;
  }

  try {
    storage.setItem(PREFIX + key, JSON.stringify(value));
    return true;
  } catch {
    // Quota, almost always. Saying so beats a silent no-op the user discovers tomorrow.
    return false;
  }
}

export function remove(key: StoreKey, area: StoreArea = 'local'): void {
  const storage = backing(area);
  try {
    storage?.removeItem(PREFIX + key);
  } catch {
    // Nothing useful to do: the value is either gone or unreachable, and both mean the same here.
  }
}

/**
 * Deletes everything this application has ever written.
 *
 * Reachable from the interface, one click, no confirmation beyond the obvious one — a promise about
 * local data that cannot be undone by the person who made it is not much of a promise. It walks the
 * keys rather than calling `clear()`, which would also delete whatever else is stored on the origin.
 */
export function clearAll(): void {
  // Both areas: "forget everything on this device" that left the current tab's draft behind would
  // be a promise kept only until the next refresh.
  for (const area of ['local', 'session'] as const) {
    const storage = backing(area);
    if (storage === null) {
      continue;
    }

    try {
      const ours = Object.keys(storage).filter((key) => key.startsWith(PREFIX));
      for (const key of ours) {
        storage.removeItem(key);
      }
    } catch {
      // Same as above.
    }
  }
}

/** Roughly how many bytes this application is using, for the interface to show what "all" means. */
export function approximateSize(): number {
  const storage = backing('local');
  if (storage === null) {
    return 0;
  }

  try {
    return Object.keys(storage)
      .filter((key) => key.startsWith(PREFIX))
      .reduce((total, key) => total + key.length + (storage.getItem(key)?.length ?? 0), 0);
  } catch {
    return 0;
  }
}
